import React, { Fragment } from 'react';
import { shape, func, bool, object } from 'prop-types';
import { connect } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, Input } from '@noths/shared-components';
import FullPageSpinner from '../../components/FullPageSpinner';
import Types from '../../redux/types';
import * as formValidations from './validations/validations';
import ErrorPanel from '../../components/ErrorPanel';
import IconEmail from '../../images/icon-email.svg';
import IconPost from '../../images/icon-post.svg';
import { errorPropType } from '../../prop-types';
import SubscribeCheckbox from '../../components/SubscribeCheckbox';

import {
  Container,
  ButtonContainer,
  ReCaptchaContainer,
  PrivacyPolicyLink,
  Name,
  FirstName,
  LastName,
  Preferences,
} from './styles';
import { isInCheckoutFlow, isInFavouritesFlow } from '../../redux/utils/redirect';
import { ROUTES } from '../../constants';

// TODO: After Sitespect test, V2 components will likely be extracted in shared-components
import * as Form from '../../components/V2/Form';
import Link from '../../components/V2/Link';
import Paragraph from '../../components/V2/Paragraph';

const defaultFieldState = {
  value: '',
  isValid: false,
  errorMessage: '',
};

export class Register extends React.Component {
  static propTypes = {
    location: object,
    register: shape({
      error: errorPropType,
      loading: bool.isRequired,
    }).isRequired,
    handleRegisterSubmit: func.isRequired,
    checkIfReCaptchaIsRequired: func.isRequired,
  };

  static defaultProps = {
    location: {},
  };

  constructor(props) {
    super();
    const { search } = props.location;

    this.state = {
      firstName: defaultFieldState,
      lastName: defaultFieldState,
      email: defaultFieldState,
      confirmEmail: defaultFieldState,
      password: defaultFieldState,
      confirmPassword: defaultFieldState,
      recaptchaChecked: false,
      marketingEmailsChecked: isInCheckoutFlow(search) || isInFavouritesFlow(search),
      marketingMailChecked: true,
      validPassword: false,
    };
  }

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(props) {
    if (props.register.error) {
      this.setState(() => ({
        password: {
          ...this.state.password,
          isValid: false,
          value: '',
        },
        confirmPassword: {
          ...this.state.confirmPassword,
          isValid: false,
          value: '',
        },
        validPassword: false,
      }));
    }
  }

  componentDidMount() {
    this.props.checkIfReCaptchaIsRequired();
  }

  getQueryParamsToRemember = () => this.props.location.search || '';

  trimFieldValue = name => {
    this.setState(prevState => ({
      [name]: {
        ...prevState[name],
        value: prevState[name].value.trim(),
      },
    }));
  };

  validateField = name => {
    this.setState(prevState => {
      const { value } = prevState[name];
      const fieldValidation = formValidations[name](value);

      return {
        [name]: {
          ...prevState[name],
          isValid: fieldValidation.isValid,
          errorMessage: fieldValidation.message,
        },
      };
    });
  };

  validateConfirmField = (fieldName, confirmFieldName) => {
    this.setState(prevState => {
      const confirmValidation = formValidations[confirmFieldName](
        this.state[fieldName].value,
        this.state[confirmFieldName].value
      );

      return {
        [confirmFieldName]: {
          ...prevState[confirmFieldName],
          isValid: confirmValidation.isValid,
          errorMessage: confirmValidation.message,
        },
      };
    });
  };

  handleFieldChange = e => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      [name]: {
        ...prevState[name],
        value,
        isValid: true,
        errorMessage: '',
      },
    }));
    if (name === 'password') {
      this.isValidPassword(value, this.state.confirmPassword.value);
    }
  };

  handleConfirmPasswordInputChange = e => {
    const { value } = e.target;
    this.setState(prevState => ({
      confirmPassword: {
        ...prevState.confirmPassword,
        value,
        isValid: true,
        errorMessage: '',
      },
    }));
    this.isValidPassword(this.state.password.value, value);
  };

  handleFieldBlur = e => {
    const { name } = e.target;
    if (name !== 'password') this.trimFieldValue(name);
    this.validateField(name);
    if (name === 'password' || name === 'confirmPassword') {
      this.validateConfirmField('password', 'confirmPassword');
    }
  };

  handleConfirmFieldBlur = (fieldName, confirmFieldName) => () => {
    this.trimFieldValue(confirmFieldName);
    this.validateConfirmField(fieldName, confirmFieldName);
  };

  toggleChecked = checkboxName => {
    this.setState(prevState => ({
      [checkboxName]: !prevState[checkboxName],
    }));
  };

  handleRecaptchaChecked = () => {
    this.setState(() => ({
      recaptchaChecked: !this.state.recaptchaChecked,
    }));
  };

  handleSubmit = e => {
    e.preventDefault();
    const fieldsToValidate = [
      'firstName',
      'lastName',
      'email',
      'confirmEmail',
      'password',
      'confirmPassword',
    ];

    const formIsValid = fieldsToValidate.every(key => this.state[key].isValid);
    if (formIsValid) {
      const {
        firstName,
        lastName,
        email,
        password,
        marketingEmailsChecked,
        marketingMailChecked,
        recaptchaChecked,
      } = this.state;

      const { requiresRecaptcha } = this.props.register;

      this.props.handleRegisterSubmit({
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        password: password.value,
        marketingEmailsChecked,
        marketingMailChecked,
        requiresRecaptcha,
        recaptchaChecked,
      });
    } else {
      ['firstName', 'lastName', 'email', 'password'].forEach(this.validateField);
      this.validateConfirmField('email', 'confirmEmail');
      this.validateConfirmField('password', 'confirmPassword');
    }
  };

  isValidPassword = (password, confirmPassword) => {
    if (
      password.length > 7 &&
      confirmPassword.length > 7 &&
      !password.includes(' ') &&
      password === confirmPassword
    ) {
      this.setState(prevState => ({
        confirmPassword: {
          ...prevState.confirmPassword,
          errorMessage: '',
        },
        validPassword: true,
      }));
    } else {
      this.setState({ validPassword: false });
    }
  };

  render() {
    const inCheckoutFlow = isInCheckoutFlow(this.props.location.search);
    const { register } = this.props;
    const queryParams = this.getQueryParamsToRemember();

    return (
      <Fragment>
        {register.loading && <FullPageSpinner />}
        <Container>
          {register.error && <ErrorPanel error={register.error} />}
          <Form.Container onSubmit={this.handleSubmit} method="post">
            <Form.Header>Register</Form.Header>
            <Paragraph textAlign="center" secondary>
              Have an account?{' '}
              <Link onClick={this.handleRegisterAnalytics} href={`${ROUTES.SIGN_IN}${queryParams}`}>
                {/* TODO: What's with query params, do we need to remember? */}
                Sign in
              </Link>
            </Paragraph>
            <Name>
              <FirstName>
                <Input
                  label="First name"
                  type="text"
                  id="firstName"
                  name="firstName"
                  size="small"
                  placeholder="Jane"
                  autoComplete="given-name"
                  value={this.state.firstName.value}
                  onBlur={this.handleFieldBlur}
                  onChange={this.handleFieldChange}
                  errorMessage={this.state.firstName.errorMessage}
                  data-first-name-field
                />
              </FirstName>
              <LastName>
                <Input
                  label="Last name"
                  type="text"
                  id="lastName"
                  name="lastName"
                  size="small"
                  placeholder="Doe"
                  autoComplete="family-name"
                  value={this.state.lastName.value}
                  onBlur={this.handleFieldBlur}
                  onChange={this.handleFieldChange}
                  errorMessage={this.state.lastName.errorMessage}
                  data-last-name-field
                />
              </LastName>
            </Name>
            <Form.InputContainer>
              <Input
                label="Email address"
                type="email"
                id="email"
                name="email"
                size="small"
                placeholder="janedoe@email.com"
                autoComplete="email"
                value={this.state.email.value}
                onBlur={this.handleFieldBlur}
                onChange={this.handleFieldChange}
                errorMessage={this.state.email.errorMessage}
                data-email-field
              />
            </Form.InputContainer>
            <Form.InputContainer>
              <Input
                label="Confirm email address"
                type="email"
                id="confirmEmail"
                name="confirmEmail"
                size="small"
                placeholder="janedoe@email.com"
                autoComplete="email"
                value={this.state.confirmEmail.value}
                onBlur={this.handleConfirmFieldBlur('email', 'confirmEmail')}
                onChange={this.handleFieldChange}
                errorMessage={this.state.confirmEmail.errorMessage}
                data-confirm-email-field
              />
            </Form.InputContainer>
            <Form.InputContainer>
              <Input
                label="Password"
                type="password"
                id="password"
                name="password"
                size="small"
                description="Must have one uppercase, one special character and one number."
                autoComplete="new-password"
                value={this.state.password.value}
                onBlur={this.handleFieldBlur}
                onChange={this.handleFieldChange}
                errorMessage={this.state.password.errorMessage}
                data-password-field
              />
            </Form.InputContainer>
            <Form.InputContainer>
              <Input
                label="Confirm password"
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                size="small"
                autoComplete="new-password"
                value={this.state.confirmPassword.value}
                onBlur={this.handleConfirmFieldBlur('password', 'confirmPassword')}
                onChange={this.handleConfirmPasswordInputChange}
                errorMessage={this.state.confirmPassword.errorMessage}
                data-confirm-password-field
              />
            </Form.InputContainer>

            <Preferences>
              <SubscribeCheckbox
                icon={IconEmail}
                intention="BE THE FIRST TO KNOW ABOUT OUR DISCOUNTS AND SALES"
                status={this.state.marketingEmailsChecked}
                data-register-marketing-email={this.state.marketingEmailsChecked}
                explainer={!this.state.marketingEmailsChecked}
                handleCheck={() => this.toggleChecked('marketingEmailsChecked')}
                description={
                  <div>
                    By ticking this box you are opting in to receiving our tailored marketing
                    emails, including discounts and sales.
                  </div>
                }
              />
              <SubscribeCheckbox
                icon={IconPost}
                purpose="receive marketing by post"
                status={this.state.marketingMailChecked}
                data-register-direct-post={this.state.marketingMailChecked}
                explainer={!this.state.marketingMailChecked}
                handleCheck={() => this.toggleChecked('marketingMailChecked')}
                description={
                  <div>
                    See our{' '}
                    <PrivacyPolicyLink href="/about/privacy" target="_blank">
                      privacy policy
                    </PrivacyPolicyLink>{' '}
                    for details about how we process your personal data to send you direct marketing
                    by post
                  </div>
                }
              />
            </Preferences>

            <Paragraph textAlign="center">
              By proceeding you confirm your acceptance of our{' '}
              <Link href="/about/privacy" target="_blank">
                Privacy Policy
              </Link>
            </Paragraph>

            {register.requiresRecaptcha && (
              <ReCaptchaContainer>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                  onChange={this.handleRecaptchaChecked}
                  data-recaptcha-checkbox
                />
              </ReCaptchaContainer>
            )}

            <ButtonContainer>
              <Button
                width="100%"
                type="submit"
                size="medium"
                disabled={register.loading}
                fullWidthOnMobile
                data-register-button
              >
                {inCheckoutFlow ? 'Continue' : 'Register'}
              </Button>
            </ButtonContainer>
          </Form.Container>
        </Container>
      </Fragment>
    );
  }
}

const mapStateToProps = ({ register, router }) => ({
  register,
  location: router.location,
});

export const mapDispatchToProps = dispatch => ({
  checkIfReCaptchaIsRequired: () => {
    dispatch({ type: Types.RECAPTCHA_CHECK });
  },
  handleRegisterSubmit: registrationDetails => {
    dispatch({
      type: Types.REGISTER_SUBMIT,
      ...registrationDetails,
    });
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Register);
